// Downloads (/download) — per-platform links from GET /releases/latest, degrading to the
// marketing /unduh page. Feed errors surfaced as muted notes (never swallow). Honest
// unsigned-installer notice (Alert, warn). Structure: shadcn-ui/ui
// apps/v4/registry/new-york-v4/ui/card.tsx + ui/alert.tsx — MIT.

import { Glyph } from "@/ui";
import { dataClass, textLinkClass } from "@ahcut/ui/recipes";
import { Alert, Button, Card, CardDescription, CardHeader, CardTitle } from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { Loading } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { getLatestRelease } from "@/lib/releases";
import { MARKETING_BASE } from "@/lib/config";
import { ApiError } from "@/lib/api";
import type { ReleasesLatest } from "@/lib/types";

// Friendly labels for Tauri updater target strings.
const PLATFORM_LABELS: Record<string, string> = {
  "windows-x86_64": "Windows (x64)",
  "darwin-aarch64": "macOS (Apple Silicon)",
  "darwin-x86_64": "macOS (Intel)",
  "linux-x86_64": "Linux (x64)",
};

function platformLabel(target: string): string {
  return PLATFORM_LABELS[target] ?? target;
}

export function Download() {
  // Best-effort: releases may be served from CDN and unavailable to this API.
  const { data, loading, error } = useAsync<ReleasesLatest>((s) => getLatestRelease(s), []);
  const marketingDownload = `${MARKETING_BASE}/unduh`;

  const platforms = data?.platforms ? Object.entries(data.platforms) : [];
  // The first-run notes follow the feed's platforms; without the feed both stay, since the
  // visitor may be on either system.
  const showWindows = platforms.length === 0 || platforms.some(([target]) => target.startsWith("windows-"));
  const showMac = platforms.length === 0 || platforms.some(([target]) => target.startsWith("darwin-"));
  // One cyan primary per view (ui/DESIGN.md §3): a single installer is the obvious
  // action; with several, the page cannot know the visitor's OS, so none is singled out.
  const platformVariant = platforms.length === 1 ? "primary" : "secondary";

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      <PageHeader
        title="Unduh aplikasi"
        lede="Aplikasi desktop ahcut gratis. Sudah punya kunci lisensi? Aktivasi di dalam aplikasi."
      />

      {loading ? (
        <Loading label="Mengecek rilis terbaru…" />
      ) : platforms.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>
              Versi terbaru
              {data?.version && (
                <span className={cn(dataClass, "ml-2 text-base font-normal text-fg-2")}>
                  {data.version}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]">
            {platforms.map(([target, asset]) => (
              <Button key={target} asChild variant={platformVariant} block>
                <a href={asset.url} target="_blank" rel="noreferrer">
                  <Glyph name="download" />
                  {asset.label ?? platformLabel(target)}
                </a>
              </Button>
            ))}
          </div>
          {data?.notes && <p className="text-sm text-fg-1">{data.notes}</p>}
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Unduh dari ahcut.com</CardTitle>
            <CardDescription>
              Installer terbaru dan spesifikasi sistem ada di halaman unduh resmi.
            </CardDescription>
          </CardHeader>
          {error && (
            // A muted note, not ErrorState: the fallback below is the recovery path.
            <p className="text-sm text-fg-2">
              Feed rilis tidak dapat dimuat
              {error instanceof ApiError ? ` (${error.code})` : ""} — gunakan halaman unduh resmi.
            </p>
          )}
          <div>
            <Button asChild variant="primary">
              <a href={marketingDownload} target="_blank" rel="noreferrer">
                Buka halaman unduh
                <Glyph name="external" />
              </a>
            </Button>
          </div>
        </Card>
      )}

      <Alert tone="warn" title="Installer belum ditandatangani">
        <div className="flex flex-col gap-2">
          <p>
            Sistem operasimu memperingatkan sekali saat ahcut pertama dipasang atau dibuka. Itu bukan
            deteksi malware: ahcut belum memakai sertifikat code-signing. Pastikan file berasal dari
            ahcut.com.
          </p>
          {showWindows && (
            <p>Di Windows, SmartScreen menampilkan peringatan: pilih More info, lalu Run anyway.</p>
          )}
          {showMac && (
            <p>
              Di Mac, seret ahcut ke Applications. Saat pertama dibuka, macOS menolaknya sekali: buka
              System Settings, pilih Privacy &amp; Security, lalu klik Open Anyway (di macOS 11 dan 12:
              System Preferences › Security &amp; Privacy, tab General).
            </p>
          )}
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            {showWindows && (
              <a
                href={`${MARKETING_BASE}/panduan/instal-ahcut-di-windows`}
                target="_blank"
                rel="noreferrer"
                className={cn(textLinkClass, "inline-flex items-center gap-1")}
              >
                Panduan instal di Windows
                <Glyph name="external" className="size-3.5" />
              </a>
            )}
            {showMac && (
              <a
                href={`${MARKETING_BASE}/panduan/instal-ahcut-di-mac`}
                target="_blank"
                rel="noreferrer"
                className={cn(textLinkClass, "inline-flex items-center gap-1")}
              >
                Panduan instal di Mac
                <Glyph name="external" className="size-3.5" />
              </a>
            )}
          </p>
        </div>
      </Alert>
    </>
  );
}
